<template>
  <div class="login-page">
    <v-container class="login-container">
      <v-card class="login-card">
        <v-card-title class="text-center text-primary text-h5 font-weight-bold">
          Login
        </v-card-title>

        <v-divider></v-divider>

        <!-- Our Login Form -->
        <v-form @submit.prevent="login" class="pa-4">
          <!-- Username -->
          <v-text-field
            v-model="email"
            label="Email"
            type="email"
            outlined
            required
            :counter="maxEmailLength"
            :error-messages="emailLengthError ? [emailLengthError] : []"
          ></v-text-field>

          <!-- Password -->
          <v-text-field
            v-model="password"
            label="Password"
            type="password"
            outlined
            required
          ></v-text-field>

          <!-- Submit Button -->
          <v-btn
            type="submit"
            color="primary"
            block
            class="mt-3"
            :loading="loading"
            :disabled="loading || Boolean(emailLengthError)"
          >
            Login
          </v-btn>

          <!-- Forgot Password Link -->
          <div class="text-center mt-2">
            <RouterLink to="/forgot-password">Forgot your password?</RouterLink>
          </div>

          <!-- Error Alert -->
          <v-alert v-if="error" type="error" class="mt-3">
            {{ error }}
          </v-alert>
        </v-form>
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { useUserStore } from "@/stores/user";

export default {
  name: "LoginPage",
  data() {
    return {
      email: "",
      password: "",
      error: "",
      loading: false,
      maxEmailLength: 254
    };
  },
  computed: {
    emailLengthError() {
      const email = this.email.trim();
      return email.length > this.maxEmailLength
        ? `Email must be ${this.maxEmailLength} characters or fewer.`
        : "";
    }
  },
  methods: {
    // Keep your getCookie helper if you need it for something else:
    getCookie(name) {
      let cookieValue = null;
      if (document.cookie && document.cookie !== "") {
        const cookies = document.cookie.split(";");
        for (let i = 0; i < cookies.length; i++) {
          const cookie = cookies[i].trim();
          if (cookie.substring(0, name.length + 1) === name + "=") {
            cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
            break;
          }
        }
      }
      return cookieValue;
    },

    async login() {
      this.error = "";
      if (this.loading) return;
      const email = this.email.trim();
      if (this.emailLengthError) {
        this.error = this.emailLengthError;
        return;
      }
      this.email = email;
      this.loading = true;
      try {
        // 1) Get the user store
        const userStore = useUserStore();
        // 2) Call the store's login action
        await userStore.login(email, this.password);

        // 3) If store didn't set user, credentials were invalid
        if (!userStore.user) {
          this.error = "Invalid credentials.";
        }
        // If login was successful, the store already redirected
        // (VIP -> /customer/profile, or else -> /home)
      } catch (err) {
        // Account locked by too many failed attempts
        if (err.response && err.response.status === 403) {
          this.error =
            'Your account has been locked due to too many failed login attempts. ' +
            'Please try again in 30 minutes, or contact support if you need immediate access.';
        }
        // Throttled: too many requests at once
        else if (err.response && err.response.status === 429) {
          const retrySecs = parseInt(err.response.headers['retry-after'], 10);
          const minutes = retrySecs ? Math.ceil(retrySecs / 60) : null;
          this.error = minutes
            ? `Too many login attempts. Please wait ${minutes} minute${minutes > 1 ? 's' : ''} and try again.`
            : 'Too many login attempts. Please try again later.';
        }
        // Other server-provided error (e.g. invalid credentials)
        else if (err.response && err.response.data && err.response.data.error) {
          this.error = err.response.data.error;
        }
        // Fallback for unexpected errors
        else {
          this.error = 'An unexpected error occurred. Please try again.';
        }
        console.error("Login error:", err);
      } finally {
        this.loading = false;
      }
    }
  },
  created() {
    // Get CSRF cookie from the server
    fetch("/api/get-csrf/")
      .then(() => {})
      .catch(err => console.error("Error setting CSRF:", err));
  }
};
</script>

<style scoped>
/* Full Background Gradient */
.login-page {
  background: linear-gradient(to bottom, #fafafa, #e7effa, #d2e3f5);
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 8vh;
  overflow: hidden;
}

/* Centered Login Container */
.login-container {
  width: 100%;
  max-width: 450px;
}

/* Login Card */
.login-card {
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out;
  width: 100%;
}

.login-card:hover {
  transform: translateY(-5px);
}

/* Form Button */
.v-btn {
  font-weight: bold;
}
</style>
